import { useSeo } from "../lib/useSeo";
import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown, Clock, Tag } from "lucide-react";
import { ASSETS, LINKS, HOURS, BEST_SELLERS, PILLARS, DEALS } from "../lib/constants";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Label, MedicalAccess, WaiverBlock, Testimonials, ProductGrid, FaqSection } from "../components/bits";
import { Marquee } from "../components/Marquee";

const ease = [0.16, 1, 0.3, 1];
const heroLines = ["Washington, DC's", "Premier Medical", "Cannabis Experience"];

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000 * 30);
    return () => clearInterval(t);
  }, []);
  const date = now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return (
    <motion.div
      className="mt-10 flex w-fit max-w-full flex-col items-start gap-3 border border-white/15 bg-ink/40 px-5 py-4 backdrop-blur-md sm:flex-row sm:items-stretch sm:gap-5"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease, delay: 1.3 }}
      data-testid="hero-hours"
    >
      <div className="sm:pr-5">
        <p className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.2em] text-gold sm:text-[10px]">
          <Clock className="h-3 w-3" /> Store Hours
        </p>
        <p className="mt-1.5 text-[11px] text-white/80 sm:text-xs">{HOURS}</p>
      </div>
      <div className="w-full border-t border-white/15 pt-3 sm:w-auto sm:border-l sm:border-t-0 sm:pl-5 sm:pt-0">
        <p className="text-[11px] text-white/60 sm:text-xs">{date}</p>
        <p className="mt-1.5 font-display text-sm text-gold sm:text-base">{time}</p>
      </div>
    </motion.div>
  );
};

export default function Home() {
  useSeo("/");
  const heroRef = useRef(null);

  const scrollToMedical = () => {
    const el = document.getElementById("medical-access");
    if (!el) return;
    const target = () => el.getBoundingClientRect().top + window.pageYOffset - 88;
    if (window.__lenis) {
      window.__lenis.scrollTo(target(), { duration: 1.2 });
      // Correct for layout shifts from lazy-loaded images above the section.
      setTimeout(() => {
        if (Math.abs(el.getBoundingClientRect().top - 88) > 60) {
          window.__lenis.scrollTo(target(), { immediate: true });
        }
      }, 1350);
    } else {
      window.scrollTo({ top: target(), behavior: "smooth" });
    }
  };
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex h-[100svh] min-h-[640px] items-center overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: videoY }} className="absolute inset-0 h-[120%]">
          <video
            className="h-full w-full object-cover"
            src={ASSETS.heroVideo}
            poster={ASSETS.exteriorWide}
            autoPlay
            muted
            loop
            playsInline
            data-testid="hero-video"
          />
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/40" />

        <div className="relative z-10 mx-auto mt-28 w-full max-w-[1400px] px-6 sm:mt-0 lg:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <Label className="!text-[9px] !tracking-[0.15em] sm:!text-xs sm:!tracking-[0.3em]">Near Union Market · Ivy City · NoMa · H Street</Label>
          </motion.div>
          <h1 className="mt-6 font-display text-3xl font-light uppercase leading-[1.08] tracking-[0.06em] text-white sm:text-4xl lg:text-5xl">
            {heroLines.map((line, i) => (
              <HeroLine key={i} delay={0.4 + i * 0.15}>{line}</HeroLine>
            ))}
          </h1>
          <motion.p
            className="mt-8 max-w-xl text-base leading-relaxed text-white/70 lg:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 1 }}
          >
            Premium flower, edibles, and vapes from a patient-first Washington, DC dispensary —
            where cannabis culture and community intersect.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: 1.15 }}
          >
            <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="hero-shop-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-champagne">
              Shop Now <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link to="/about" data-testid="hero-about-btn" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-gold hover:text-gold">
              Our Story
            </Link>
            <button
              onClick={scrollToMedical}
              data-testid="hero-medcard-btn"
              className="inline-flex items-center gap-2 border border-gold/50 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              Need a Medical Card? <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
          <HeroClock />
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 md:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
        >
          <div className="flex h-12 w-7 items-start justify-center rounded-full border border-white/25 p-1.5">
            <motion.span className="h-2 w-1 rounded-full bg-gold" animate={{ y: [0, 12, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
          </div>
        </motion.div>
      </section>

      <Marquee items={["Flower", "Prerolls", "Vapes", "Cartridges", "Concentrates", "Edibles", "Tinctures"]} />

      {/* THIS WEEK'S DEALS */}
      <section className="border-t border-white/10 bg-ink py-16 md:py-24" data-testid="deals-band">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <Reveal><Label>This Week's Deals</Label></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
                  This Week's Deals
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link to="/events" data-testid="deals-events-btn" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                Upcoming Events <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {DEALS.map((d, i) => (
              <StaggerItem key={i}>
                <div className="group flex h-full flex-col border border-white/10 bg-ink-2 p-8 transition-all duration-500 hover:border-gold/50" data-testid={`deal-card-${i}`}>
                  <Tag className="h-6 w-6 text-gold" />
                  <p className="mt-6 font-display text-xl font-light uppercase leading-snug tracking-[0.04em] text-white">{d.deal}</p>
                  <p className="mt-4 text-xs uppercase tracking-[0.12em] text-white/45">{d.fine}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MANIFESTO INTRO */}
      <section className="bg-ink py-24 md:py-36" data-testid="home-intro">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <Reveal><Label>About Us</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl font-light uppercase leading-tight tracking-[0.06em] text-white md:text-5xl">
                Where Cannabis Culture & Community Intersect
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 leading-relaxed text-white/60">
                More than a medical cannabis dispensary, Higher Ground DC is a thoughtfully designed space
                where wellness, education, and lifestyle come together. We are creating a one-of-a-kind
                destination centered on a patient-first experience, premium products, and a deep respect
                for the plant and its culture.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <Link to="/about" data-testid="intro-more-btn" className="mt-8 inline-flex items-center gap-2 border-b border-gold pb-1 text-xs uppercase tracking-[0.22em] text-gold transition-all hover:gap-3">
                Read Our Story <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={ASSETS.exteriorMural} alt="Higher Ground DC dispensary mural in Washington DC" className="h-full w-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="border-t border-white/10 bg-ink-2 py-20 md:py-28" data-testid="best-sellers">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal><Label>Best Sellers</Label></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">
                  What Patients Ask For Most
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="bestsellers-viewall" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold transition-colors hover:text-champagne">
                View All <ArrowUpRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl leading-relaxed text-white/55">
              A curated mix of premium flower, vape options, and edibles so you can compare effects, potency,
              and format without starting from scratch. Our live menu refreshes throughout the day.
            </p>
          </Reveal>

          <div className="mt-14">
            <ProductGrid testPrefix="product-card" />
          </div>
        </div>
      </section>

      {/* EDITORIAL SPOTLIGHT */}
      <section className="relative overflow-hidden border-t border-white/10 bg-ink py-24 md:py-36" data-testid="home-spotlight">
        <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src={ASSETS.pinkFoxBag} alt="Pink Fox — DC's original edible, handmade in Washington DC" className="h-full w-full object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
            </div>
          </Reveal>
          <div>
            <Reveal><Label>Curated & Local</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl font-light uppercase leading-tight tracking-[0.06em] text-white md:text-5xl">
                Craft Products, Curated With Intention
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-8 leading-relaxed text-white/60">
                From DC's original handmade edibles to top-shelf flower, every product on our shelves is
                selected for quality, transparency, and effect. We partner with makers who share our
                obsession with craft — so every visit feels considered.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <Link to="/media" data-testid="spotlight-media-btn" className="mt-8 inline-flex items-center gap-2 border-b border-gold pb-1 text-xs uppercase tracking-[0.22em] text-gold transition-all hover:gap-3">
                Explore The Gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — NUMBERED CHAPTERS */}
      <section className="border-t border-white/10 bg-ink-2 py-20 md:py-28" data-testid="why-choose-us">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal><Label>The Higher Ground DC Difference</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-3xl font-light uppercase tracking-[0.08em] text-white md:text-4xl">Why Choose Us</h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={0.1 * i}>
                <div className="group flex h-full flex-col bg-ink p-8 transition-colors duration-500 hover:bg-ink-3 md:p-12">
                  <span className="font-display text-6xl font-extralight text-gold/30 transition-colors duration-500 group-hover:text-gold md:text-7xl">{p.n}</span>
                  <h3 className="mt-6 font-display text-xl font-light uppercase tracking-[0.1em] text-white">{p.title}</h3>
                  <p className="mt-4 leading-relaxed text-white/55">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MedicalAccess />
      <Testimonials />
      <WaiverBlock />
      <FaqSection />

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-white/10" data-testid="home-cta">
        <div className="absolute inset-0">
          <img src={ASSETS.exteriorWide} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/80" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 py-24 text-center lg:px-12 md:py-36">
          <Reveal><Label>Nation's Capital</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mx-auto mt-5 max-w-4xl font-display text-3xl font-light uppercase leading-tight tracking-[0.06em] text-white md:text-5xl">
              Ready for the Peak of Cannabis in DC?
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="cta-shop-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                Shop Online <ArrowUpRight className="h-4 w-4" />
              </a>
              <Link to="/contact" data-testid="cta-visit-btn" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                Visit Our Store
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
